import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Button } from 'shared/ui/button'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Switch } from 'shared/ui/switch'
import { Checkbox } from 'shared/ui/checkbox'
import { Separator } from 'shared/ui/separator'
import { Eye, EyeOff, Server, Shield, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  createLdapAuthConfig,
  searchLdapAuthConfigs,
  updateLdapAuthConfig,
} from 'modules/admin/api/ldapAuthConfigService'

interface AdConfig {
  id?: string
  name: string
  host: string
  port: number
  useSsl: boolean
  useTls: boolean
  baseDn: string
  bindDn: string
  bindPassword: string
  userFilter: string
  usernameAttr: string
  isActive: boolean
}

export function ActiveDirectoryConfiguration() {
  const [config, setConfig] = useState<AdConfig>({
    id: undefined,
    name: 'IRPC AD',
    host: 'ldap://dc01.company.local',
    port: 389,
    useSsl: false,
    useTls: false,
    baseDn: 'DC=company,DC=local',
    bindDn: '',
    bindPassword: '',
    userFilter: '(sAMAccountName={username})',
    usernameAttr: 'sAMAccountName',
    isActive: true,
  })
  const [showPassword, setShowPassword] = useState(false)
  // Test connection is not implemented; hide related state/UI
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const update = (patch: Partial<AdConfig>) => setConfig((prev) => ({ ...prev, ...patch }))

  const loadExisting = async () => {
    try {
      setIsLoading(true)
      const res = await searchLdapAuthConfigs({ page: 1, pageSize: 1 })
      const first = res.items?.[0]
      if (first) {
        setConfig({
          id: first.ldapAuthConfigId,
          name: first.name,
          host: first.host,
          port: first.port,
          useSsl: first.useSsl,
          useTls: first.useTls,
          baseDn: first.baseDn,
          bindDn: first.bindDn ?? '',
          bindPassword: first.bindPassword ?? '',
          userFilter: first.userFilter,
          usernameAttr: first.usernameAttr,
          isActive: first.isActive,
        })
      }
    } catch (err) {
      console.error(err)
      toast.error('โหลดการตั้งค่า AD ไม่สำเร็จ')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadExisting()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload = {
        tenantId: undefined,
        name: config.name.trim(),
        host: config.host.trim(),
        port: config.port || 389,
        useSsl: config.useSsl,
        useTls: config.useTls,
        baseDn: config.baseDn.trim(),
        bindDn: config.bindDn?.trim() || null,
        bindPassword: config.bindPassword || null,
        userFilter: config.userFilter.trim(),
        usernameAttr: config.usernameAttr.trim() || 'sAMAccountName',
        isActive: config.isActive,
      }

      if (config.id) {
        const updated = await updateLdapAuthConfig(config.id, payload)
        setConfig((prev) => ({ ...prev, id: updated.ldapAuthConfigId }))
        toast.success('บันทึกการตั้งค่า AD สำเร็จ')
      } else {
        try {
          const created = await createLdapAuthConfig(payload)
          setConfig((prev) => ({ ...prev, id: created.ldapAuthConfigId }))
          toast.success('สร้างการตั้งค่า AD สำเร็จ')
        } catch (err: any) {
          // ถ้า duplicated/409 ให้โหลดรายการเดิมมาแล้วอัปเดตแทน
          if (err?.response?.status === 409) {
            await loadExisting()
            const existingId = config.id
            if (existingId) {
              const updated = await updateLdapAuthConfig(existingId, payload)
              setConfig((prev) => ({ ...prev, id: updated.ldapAuthConfigId }))
              toast.success('บันทึกการตั้งค่า AD สำเร็จ')
            } else {
              toast.error('บันทึกการตั้งค่า AD ไม่สำเร็จ (duplicate)')
            }
          } else {
            throw err
          }
        }
      }
    } catch (err) {
      console.error(err)
      toast.error('บันทึกการตั้งค่า AD ไม่สำเร็จ')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="border-blue-200 bg-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-800">
          <Shield className="w-5 h-5" />
          Active Directory Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <Server className="w-4 h-4" />
          <AlertDescription>
            ตั้งค่าการเชื่อมต่อ AD สำหรับการยืนยันตัวตน (เฉพาะฟิลด์จำเป็น)
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label>ชื่อการเชื่อมต่อ</Label>
              <Input value={config.name} onChange={(e) => update({ name: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Host / URL</Label>
                <Input
                  value={config.host}
                  onChange={(e) => update({ host: e.target.value })}
                  placeholder="ldap://dc01.company.local"
                />
              </div>
              <div>
                <Label>Port</Label>
                <Input
                  type="number"
                  value={config.port}
                  onChange={(e) => update({ port: parseInt(e.target.value) || 389 })}
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={config.useSsl} onCheckedChange={(checked) => update({ useSsl: checked })} />
                <Label className="cursor-pointer">Use SSL (LDAPS)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={config.useTls} onCheckedChange={(checked) => update({ useTls: checked })} />
                <Label className="cursor-pointer">Use StartTLS</Label>
              </div>
            </div>
            <div>
              <Label>Base DN</Label>
              <Input
                value={config.baseDn}
                onChange={(e) => update({ baseDn: e.target.value })}
                placeholder="DC=company,DC=local"
              />
            </div>
            <div>
              <Label>User Filter</Label>
              <Input
                value={config.userFilter}
                onChange={(e) => update({ userFilter: e.target.value })}
                placeholder="(sAMAccountName={username})"
              />
            </div>
            <div>
              <Label>Username Attribute</Label>
              <Input
                value={config.usernameAttr}
                onChange={(e) => update({ usernameAttr: e.target.value })}
                placeholder="sAMAccountName"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="isActive"
                checked={config.isActive}
                onCheckedChange={(checked) => update({ isActive: !!checked })}
              />
              <Label htmlFor="isActive">ใช้งานการเชื่อมต่อ (Active)</Label>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Bind DN (service account)</Label>
              <Input
                value={config.bindDn}
                onChange={(e) => update({ bindDn: e.target.value })}
                placeholder="CN=service,OU=Service Accounts,DC=company,DC=local"
              />
            </div>
            <div>
              <Label>Bind Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={config.bindPassword}
                  onChange={(e) => update({ bindPassword: e.target.value })}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <Separator />

            <div className="flex items-center gap-2">
              <Button onClick={handleSave} disabled={isSaving || isLoading}>
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              รองรับการ bind ตรวจสอบ AD เท่านั้น ข้อมูลผู้ใช้ละเอียดจะดึง/แมป ในขั้นตอนต่อไป
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default ActiveDirectoryConfiguration
